import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../../components/Logo';
import { FullPageStatus } from '../../components/FullPageStatus';
import { NotFoundPage } from '../../components/NotFoundPage';
import { PageHeader } from '../../components/PageHeader';
import { AdminAccounts } from './AdminAccounts';
import { AdminBusinessList } from './AdminBusinessList';
import { AdminFlagQueue } from './AdminFlagQueue';
import { AdminLearning } from './AdminLearning';
import { AdminTemplateQueue } from './AdminTemplateQueue';
import { useAdminStatus } from './useAdminStatus';

type AdminTab = 'businesses' | 'accounts' | 'flags' | 'templates' | 'learning';

const TABS: readonly { key: AdminTab; label: string }[] = [
  { key: 'businesses', label: 'Businesses' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'flags', label: 'Flag queue' },
  { key: 'templates', label: 'Template reviews' },
  { key: 'learning', label: 'Learning' },
];

/**
 * The staff panel. Five jobs: adjust a business's monthly allowance, set an
 * account's plan by hand (Accounts), answer the flag queue, review community
 * templates for the shared gallery, and read whether brand memory is helping
 * (Learning).
 *
 * Deliberately outside the customer shell — no sidebar, no quota counter, no
 * command palette. An operator is looking at other people's businesses, and
 * chrome that says "your posts" would be lying about whose screen this is.
 * It is also not in the nav or the palette: the route is reached by typing it,
 * and a non-operator who does gets the same 404 as a route that does not exist,
 * because that is what the backend tells them.
 */
export function AdminPage() {
  const { isChecking, isAdmin, email } = useAdminStatus();
  const [tab, setTab] = useState<AdminTab>('businesses');

  if (isChecking) {
    return <FullPageStatus message="Checking access" />;
  }

  if (!isAdmin) {
    return <NotFoundPage />;
  }

  return (
    <div className="admin-shell">
      <header className="admin-shell__header">
        <span className="admin-shell__mark">
          <Logo />/ operator
        </span>
        <span className="admin-shell__email">{email}</span>
        <Link className="button button--ghost" to="/">
          Back to the app
        </Link>
      </header>

      <main className="page admin-shell__content">
        <PageHeader
          section="Operator"
          title="Staff panel"
          lede="Allowances, plans, reports, template reviews and learning. Nothing here changes what a pipeline produced."
        />

        <div className="tabs" role="tablist" aria-label="Admin sections">
          {TABS.map((item) => (
            <button
              key={item.key}
              className={item.key === tab ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
              type="button"
              role="tab"
              aria-selected={item.key === tab}
              onClick={() => setTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === 'businesses' ? (
          <AdminBusinessList />
        ) : tab === 'accounts' ? (
          <AdminAccounts />
        ) : tab === 'flags' ? (
          <AdminFlagQueue />
        ) : tab === 'templates' ? (
          <AdminTemplateQueue />
        ) : (
          <AdminLearning />
        )}
      </main>
    </div>
  );
}
